var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import classNames from 'classnames';
import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import useGenericMessageSubscription from 'hooks/useGenericMessageSubscription';
import useKeyDownListener from 'hooks/useKeyDownListener';
import { usePrevious } from 'hooks/usePrevious';
import useWebSocketMessage from 'hooks/useWebSocketMessage';
import PaperclipSvg from 'images/v5/icons/attachment-01.svg';
import LinkSvg from 'images/v5/icons/link-03.svg';
import PlusSvg from 'images/v5/icons/plus.svg';
import SendSvg from 'images/v5/icons/send-01.svg';
import CloseSvg from 'images/v5/icons/x-close.svg';
import Avatar from 'layout/atoms/Avatar/Avatar';
import Button from 'layout/atoms/Button/Button';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import FileInput from 'layout/atoms/FileInput';
import Input from 'layout/atoms/Input/Input';
import InputLabel from 'layout/atoms/InputLabel';
import Loader from 'layout/atoms/Loader/Loader';
import MentionsDropdown from 'layout/atoms/MentionsDropdown/MentionsDropdown';
import Message from 'layout/atoms/Message/Message';
import PortalBackdrop from 'layout/atoms/PortalBackdrop/PortalBackdrop';
import SystemAlert from 'layout/atoms/SystemAlert';
import { addChat, addChatMember, addChatMessage, fetchChat, fetchMembers, removeChatMember, resetChatState, selectChat, selectChatFollowers, selectChatMembers, selectChatMessages, selectChatRefetchFlags, selectChatStatus, } from 'store/chatSlice';
import { clearUnreadCountForConversation, resetConversationUnreadCount } from 'store/inboxSlice';
import { activateSidebar, closeModal, closeSidebar, removeSidebar, selectSidebarByName, } from 'store/layoutSlice';
import { selectFolderName } from 'store/specificationsFoldersSlice';
import { selectUserProfileDataData } from 'store/userSlice';
import env from 'utils/env';
import { getEnhancedMessages } from 'utils/getEnhancedMessages';
import styles from './Chat.module.scss';
function Chat() {
    var _a, _b;
    const { t, i18n } = useTranslation(['specification', 'common']);
    const dispatch = useDispatch();
    const copyToClipboard = useCopyToClipboard();
    const sidebar = useSelector(selectSidebarByName('chat'));
    const chatUrl = (_a = sidebar === null || sidebar === void 0 ? void 0 : sidebar.data) === null || _a === void 0 ? void 0 : _a.id;
    const chatId = useSelector(selectChat).id;
    const pdfUrl = chatUrl.startsWith('/specifications/details')
        ? chatUrl.replace('/specifications/details/', '/specifications/pdf/').replace('/summary', '')
        : false;
    const messages = useSelector(selectChatMessages);
    const enhancedMessages = getEnhancedMessages(messages);
    const followers = useSelector(selectChatFollowers);
    const followersIds = followers === null || followers === void 0 ? void 0 : followers.map((item) => item.user);
    const allMembers = useSelector(selectChatMembers);
    const currentUser = useSelector(selectUserProfileDataData);
    const status = useSelector(selectChatStatus);
    const refetchFlags = useSelector(selectChatRefetchFlags);
    const members = useMemo(() => {
        if (currentUser === null || currentUser === void 0 ? void 0 : currentUser.id) {
            return allMembers.filter((member) => member.id !== currentUser.id);
        }
        return allMembers;
    }, [allMembers, currentUser]);
    // WebSocket hooks
    const { sendMessage: sendWebSocketMessage } = useWebSocketMessage();
    useGenericMessageSubscription(chatId, 'chat');
    const rootFolder = useSelector((state) => state.specificationsFolders.rootFolder);
    const folderId = chatUrl.includes('/specifications/folders/')
        ? chatUrl.replace('/specifications/folders/', '')
        : rootFolder === null || rootFolder === void 0 ? void 0 : rootFolder.rootFolderId;
    const folderName = useSelector((state) => selectFolderName(state, folderId));
    const folderDisplayName = folderId === rootFolder.rootFolderId ? t('all_folders') : folderName;
    const specificationNameFromDetails = useSelector((state) => { var _a, _b; return (_b = (_a = state.specificationDetails) === null || _a === void 0 ? void 0 : _a.item) === null || _b === void 0 ? void 0 : _b.fancyName; });
    const specificationNameFromSidebar = (_b = sidebar === null || sidebar === void 0 ? void 0 : sidebar.data) === null || _b === void 0 ? void 0 : _b.title;
    const specificationNameFromChat = useSelector((state) => { var _a; return (_a = state.chat) === null || _a === void 0 ? void 0 : _a.title; });
    const specificationName = specificationNameFromDetails ||
        specificationNameFromSidebar ||
        specificationNameFromChat ||
        '-';
    const [form, setForm] = useState({
        conversationId: chatId,
        message: '',
        image: null,
    });
    const [mentionsState, setMentionsState] = useState({
        showDropdown: false,
        filteredUsers: [],
        selectedIndex: 0,
        mentionStart: 0,
        searchText: '',
        mentions: [],
    });
    const [mentionedUsers, setMentionedUsers] = useState(new Map());
    const chatWindow = useRef(null);
    const inputRef = useRef(null);
    const { opened, active } = sidebar || {};
    const prevActive = usePrevious(active);
    const classes = classNames(styles.wrap, { [styles.active]: active });
    useEffect(() => {
        if (opened) {
            setTimeout(() => {
                dispatch(activateSidebar({ name: 'chat' }));
            }, 10);
        }
    }, [dispatch, opened]);
    useEffect(() => {
        if (prevActive && !active) {
            setTimeout(() => {
                dispatch(removeSidebar({ name: 'chat' }));
            }, 350);
        }
    }, [dispatch, active, prevActive]);
    useEffect(() => {
        if (chatWindow.current) {
            chatWindow.current.scrollTo({
                top: chatWindow.current.scrollHeight,
                behavior: 'auto',
            });
        }
        if (status.fetchMembers === 'idle') {
            dispatch(fetchMembers());
            moment.updateLocale(i18n.language, {
                relativeTime: {},
            });
        }
        if (status.fetch === 'idle' || refetchFlags.fetch) {
            dispatch(fetchChat({ url: chatUrl }));
        }
    }, [dispatch, i18n, chatUrl, status, refetchFlags]);
    useEffect(() => {
        dispatch(addChat({
            url: chatUrl,
            title: chatUrl.startsWith('/specifications/details')
                ? specificationName
                : folderDisplayName,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, chatUrl]);
    // Clear unread count when chat loads successfully
    useEffect(() => {
        if (chatId && status.fetch === 'succeeded') {
            // Clear local unread count
            dispatch(clearUnreadCountForConversation({ conversationId: chatId }));
            // Reset server-side unread count
            dispatch(resetConversationUnreadCount(chatId));
        }
    }, [dispatch, chatId, status.fetch]);
    useEffect(() => {
        return () => {
            dispatch(resetChatState());
        };
    }, [dispatch]);
    const extractMentionsFromText = (text) => {
        const mentionRegex = /@(\w+)/g;
        const mentions = [];
        const matches = Array.from(text.matchAll(mentionRegex));
        matches.forEach((match) => {
            const username = match[1];
            const mentionData = mentionedUsers.get(username);
            if (mentionData) {
                mentions.push(mentionData);
            }
        });
        return mentions;
    };
    const handleAddMessage = () => __awaiter(this, void 0, void 0, function* () {
        if (form.message !== '') {
            if (chatWindow.current) {
                chatWindow.current.scrollTo({
                    top: chatWindow.current.scrollHeight,
                    behavior: 'smooth',
                });
            }
            const mentions = extractMentionsFromText(form.message);
            const messageToSend = form.message;
            // Clear the form immediately
            setForm(Object.assign(Object.assign({}, form), { message: '' }));
            setMentionsState((prev) => (Object.assign(Object.assign({}, prev), { mentions: [] })));
            setMentionedUsers(new Map());
            try {
                yield sendWebSocketMessage({
                    conversationId: chatId,
                    text: messageToSend,
                    image: form.image ? form.image.name : undefined,
                    mentions: mentions.length > 0 ? mentions : undefined,
                });
            }
            catch (error) {
                // Fallback to HTTP if WebSocket fails
                dispatch(addChatMessage({
                    conversationId: chatId,
                    text: messageToSend,
                    image: form.image,
                    mentions: mentions.length > 0 ? mentions : undefined,
                }));
            }
        }
    });
    const handleAddImage = useCallback((chosenFile) => {
        if (!chosenFile)
            return;
        dispatch(addChatMessage({
            conversationId: chatId,
            text: form.message,
            image: chosenFile,
        }));
    }, [dispatch, chatId, form.message]);
    const handleAddFollower = (id) => {
        dispatch(addChatMember({ conversationId: chatId, userId: id }));
    };
    const handleRemoveFollower = (id) => {
        dispatch(removeChatMember({ conversationId: chatId, userId: id }));
    };
    const handleSelectMention = (user) => {
        var _a;
        const beforeMention = form.message.substring(0, mentionsState.mentionStart);
        const afterMention = form.message.substring(((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.selectionStart) || form.message.length);
        const mentionText = `@${user.username}`;
        const newMessage = beforeMention + mentionText + afterMention;
        setForm(Object.assign(Object.assign({}, form), { message: newMessage }));
        setMentionsState((prev) => (Object.assign(Object.assign({}, prev), { showDropdown: false })));
        // Store the mention data for later conversion
        setMentionedUsers((prev) => {
            const updated = new Map(prev);
            updated.set(user.username, { id: user.id, username: user.username });
            return updated;
        });
        setTimeout(() => {
            if (inputRef.current) {
                const newCursorPos = beforeMention.length + mentionText.length;
                inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
                inputRef.current.focus();
            }
        }, 0);
    };
    const handleKeyDown = (e) => {
        if (mentionsState.showDropdown) {
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setMentionsState((prev) => (Object.assign(Object.assign({}, prev), { selectedIndex: Math.min(prev.selectedIndex + 1, prev.filteredUsers.length - 1) })));
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setMentionsState((prev) => (Object.assign(Object.assign({}, prev), { selectedIndex: Math.max(prev.selectedIndex - 1, 0) })));
                    break;
                case 'Enter':
                case ' ':
                    e.preventDefault();
                    if (mentionsState.filteredUsers[mentionsState.selectedIndex]) {
                        handleSelectMention(mentionsState.filteredUsers[mentionsState.selectedIndex]);
                    }
                    break;
                case 'Escape':
                    setMentionsState((prev) => (Object.assign(Object.assign({}, prev), { showDropdown: false })));
                    break;
                default:
                    break;
            }
        }
        else if (e.key === 'Enter') {
            handleAddMessage();
        }
    };
    const handleInputChange = (e) => {
        const { value, selectionStart } = e.target;
        setForm(Object.assign(Object.assign({}, form), { message: value }));
        const cursorPos = selectionStart || 0;
        const textBeforeCursor = value.substring(0, cursorPos);
        const lastAtIndex = textBeforeCursor.lastIndexOf('@');
        if (lastAtIndex !== -1) {
            const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
            if (!/\s/.test(textAfterAt)) {
                const filteredUsers = members
                    .filter((member) => member.username.toLowerCase().includes(textAfterAt.toLowerCase()))
                    .slice(0, 5);
                setMentionsState({
                    showDropdown: filteredUsers.length > 0,
                    filteredUsers,
                    selectedIndex: 0,
                    mentionStart: lastAtIndex,
                    searchText: textAfterAt,
                    mentions: mentionsState.mentions,
                });
            }
            else {
                setMentionsState((prev) => (Object.assign(Object.assign({}, prev), { showDropdown: false })));
            }
        }
        else {
            setMentionsState((prev) => (Object.assign(Object.assign({}, prev), { showDropdown: false })));
        }
    };
    useKeyDownListener([
        {
            code: 'Enter',
            callback: () => !mentionsState.showDropdown && handleAddMessage(),
        },
    ]);
    if (status.fetch === 'idle' || status.fetch === 'loading') {
        return (React.createElement(PortalBackdrop, { opened: opened, active: active },
            React.createElement("div", { className: classes },
                React.createElement("header", { className: styles.header },
                    React.createElement("div", { className: styles.title },
                        React.createElement("span", null, chatUrl.startsWith('/specifications/details')
                            ? t('chat_header_specification')
                            : t('chat_header_folder')),
                        React.createElement("h3", null, chatUrl.startsWith('/specifications/details')
                            ? specificationName
                            : folderDisplayName)),
                    React.createElement(Button, { theme: "ghost", shape: "squared", onClick: () => dispatch(closeSidebar({ name: 'chat' })) },
                        React.createElement(CloseSvg, null))),
                React.createElement("main", { className: styles.main, ref: chatWindow },
                    React.createElement(Loader, null)))));
    }
    return (React.createElement(PortalBackdrop, { opened: opened, active: active },
        React.createElement("div", { className: classes },
            React.createElement("header", { className: styles.header },
                React.createElement("div", { className: styles.title },
                    React.createElement("span", null, chatUrl.startsWith('/specifications/details')
                        ? t('chat_header_specification')
                        : t('chat_header_folder')),
                    React.createElement("h3", null, chatUrl.startsWith('/specifications/details')
                        ? specificationName
                        : folderDisplayName)),
                React.createElement(Button, { theme: "ghost", shape: "squared", onClick: () => dispatch(closeSidebar({ name: 'chat' })) },
                    React.createElement(CloseSvg, null))),
            pdfUrl && (React.createElement(SystemAlert, { leftIcon: React.createElement(LinkSvg, null), aside: React.createElement(React.Fragment, null,
                    React.createElement(Button, { size: "xs", onClick: () => copyToClipboard(`${window.location.origin}${pdfUrl}`) }, t('COPY_URL_BUTTON')),
                    React.createElement(Button, { size: "xs", to: pdfUrl, onClick: () => {
                            dispatch(closeSidebar({ name: 'chat' }));
                            dispatch(closeModal({ name: 'specificationCreator' }));
                        } }, t('SHOW_PDF_BUTTON'))) }, t('SHOW_PDF_TEXT'))),
            React.createElement("main", { className: classNames(styles.main, { [styles.pdfUrl]: pdfUrl }), ref: chatWindow },
                enhancedMessages &&
                    enhancedMessages.map((message) => (React.createElement(Message, { key: message.id, id: message.id, author: message.sender.userName, text: message.text, picture: message.image, date: message.createdDate, sendByMe: message.sendByMe, showName: message.showName, showDate: message.showDate }))),
                status.addMessage === 'loading' && React.createElement(Loader, null)),
            React.createElement("footer", { className: styles.footer },
                React.createElement("div", { className: styles.input },
                    React.createElement("div", { style: { position: 'relative' } },
                        React.createElement(Input, { placeholder: t('enter_comment'), value: form.message, onChange: handleInputChange, onKeyDown: handleKeyDown, ref: inputRef }),
                        React.createElement(MentionsDropdown, { users: mentionsState.filteredUsers, selectedIndex: mentionsState.selectedIndex, onSelect: handleSelectMention, visible: mentionsState.showDropdown, inputRef: inputRef })),
                    React.createElement(FileInput, { allowedExtensions: ['jpg', 'png', 'jpeg'], onSelect: handleAddImage }, (openFileChooser) => (React.createElement(Button, { shape: "rounded", leftIcon: React.createElement(PaperclipSvg, null), onClick: openFileChooser }))),
                    React.createElement(Button, { color: "primary", shape: "rounded", leftIcon: React.createElement(SendSvg, null), onClick: handleAddMessage })),
                React.createElement("div", { className: styles.followers },
                    React.createElement(InputLabel, { text: t('followers') }),
                    React.createElement("div", { className: styles.followersList }, followers === null || followers === void 0 ? void 0 :
                        followers.map((follower) => {
                            var _a;
                            return (React.createElement(Avatar, { key: follower.id, shape: "round", userName: follower.email, src: ((_a = follower === null || follower === void 0 ? void 0 : follower.user) === null || _a === void 0 ? void 0 : _a.avatar) && `${env.static}/${follower.user.avatar}`, onClick: () => handleRemoveFollower(follower.id) }));
                        }),
                        React.createElement(Dropdown, { position: "topRight", trigger: React.createElement(Button, { color: "primary", shape: "rounded" },
                                React.createElement(PlusSvg, null)) }, _.differenceBy(members, followersIds, 'id').map((member) => (React.createElement(Button, { key: member.id, align: "left", theme: "ghost", leftIcon: React.createElement(Avatar, { size: "s", shape: "round", src: member.avatar && `${env.static}/${member.avatar}`, userName: member.username }), onClick: () => handleAddFollower(member.id) }, member.username))))))))));
}
export default Chat;
